import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { invokeFunction } from "@/lib/invoke";
import { cancelOverrideBody, type CancelOverride } from "@/lib/cancel-balance";

/**
 * Active MP Preapproval (recurring subscription) for an organization.
 * Mirrors a row in public.mp_preapprovals — RLS limits the read to org
 * members. The webhook is what writes status changes; the hook is read-only.
 */
export interface PreapprovalRow {
  id: string;
  organization_id: string;
  mp_preapproval_id: string;
  plan_id: string;
  status: string; // pending | authorized | paused | cancelled | expired
  monthly_amount: number;
  payer_email: string;
  card_last_four: string | null;
  card_brand: string | null;
  /** MP-hosted authorization URL. Set on INSERT in mercadopago-preapproval-create
   *  so the UI can show a "resume authorization" link when the user closes the
   *  tab on a pending preapproval. Null on rows created via webhook fallback. */
  init_point: string | null;
  last_charge_at: string | null;
  last_charge_amount: number | null;
  last_charge_status: string | null;
  mode?: string | null;
  last_error: string | null;
  card_changed_at?: string | null;
  mp_verified_status?: string | null;
  created_at: string;
  updated_at: string;
  cancelled_at: string | null;
}

export interface ChangeCardOutcome {
  resumed: boolean;
}

export function useSubscriptionPreapproval(orgId: string | null) {
  const [preapproval, setPreapproval] = useState<PreapprovalRow | null>(null);
  const [loading, setLoading] = useState(true);
  // `error` is set when the supabase call itself fails (RLS rejection, network
  // glitch, etc.) so the UI can distinguish "no active preapproval" from
  // "couldn't load it". The empty-state alert and the error-state alert are
  // different copy.
  const [error, setError] = useState<Error | null>(null);

  const fetchPreapproval = useCallback(async () => {
    if (!orgId) {
      setPreapproval(null);
      setLoading(false);
      setError(null);
      return;
    }
    // Re-set loading on every fetch so a workspace switch shows the loader
    // again instead of momentarily flashing the empty-state placeholder.
    setLoading(true);
    setError(null);
    // We treat the most recent non-cancelled row as the active one. There's
    // at most one of those per org (the create function rejects with 409 if
    // another pending/authorized row exists, and the partial unique index
    // enforces it at the DB level too), but ordering by created_at makes the
    // query robust to manual cleanups too.
    const { data, error: fetchErr } = await supabase
      .from("mp_preapprovals")
      .select("*")
      .eq("organization_id", orgId)
      .is("cancelled_at", null)
      .in("status", ["pending", "authorized", "paused"])
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (fetchErr) {
      console.error("[useSubscriptionPreapproval] fetch error:", fetchErr);
      setError(fetchErr instanceof Error ? fetchErr : new Error(String(fetchErr)));
      setPreapproval(null);
      setLoading(false);
      return;
    }
    setPreapproval((data as PreapprovalRow | null) || null);
    setLoading(false);
  }, [orgId]);

  useEffect(() => { fetchPreapproval(); }, [fetchPreapproval]);

  const startAuthorize = useCallback(
    async (planId: string, payerEmail?: string, mode?: string): Promise<{ init_point: string; mp_preapproval_id: string }> => {
      if (!orgId) throw new Error("No organization");
      const body: Record<string, unknown> = { plan_id: planId, organization_id: orgId };
      if (payerEmail) body.payer_email = payerEmail;
      if (mode) body.mode = mode;
      const { data, error, code } = await invokeFunction<{ init_point: string; mp_preapproval_id: string }>(
        "mercadopago-preapproval-create",
        body,
      );
      if (error) throw Object.assign(new Error(error), { code });
      const result = data as { init_point: string; mp_preapproval_id: string };
      if (!result?.init_point) throw new Error("No init_point returned");
      return result;
    },
    [orgId],
  );

  const cancel = useCallback(async (override?: CancelOverride) => {
    if (!preapproval) throw new Error("No active preapproval to cancel");
    const { error, code } = await invokeFunction("mercadopago-preapproval-cancel", {
      mp_preapproval_id: preapproval.mp_preapproval_id,
      ...cancelOverrideBody(override),
    });
    if (error) throw Object.assign(new Error(error), { code: code ?? null });
    await fetchPreapproval();
  }, [preapproval, fetchPreapproval]);

  const changeCard = useCallback(async (cardTokenId: string): Promise<ChangeCardOutcome> => {
    if (!orgId) throw new Error("No organization");
    const { data, error, code } = await invokeFunction<{ resumed?: boolean }>(
      "mercadopago-preapproval-change-card",
      { organization_id: orgId, card_token_id: cardTokenId },
    );
    if (error) throw Object.assign(new Error(error), { code: code ?? null });
    await fetchPreapproval();
    return { resumed: data?.resumed === true };
  }, [orgId, fetchPreapproval]);

  return {
    preapproval,
    loading,
    error,
    refetch: fetchPreapproval,
    startAuthorize,
    cancel,
    changeCard,
  };
}
